import { InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';
import { loginRequest, msalConfig } from './authConfig';

// MSAL documentation links:
// https://github.com/AzureAD/microsoft-authentication-library-for-js/tree/dev/lib/msal-browser
// https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/initialization.md
// https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
// https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/images/msaljs-boot-flow.png
// NOTE: The flow diagram above suggests ssoSilent should be called when there are no accounts.

/**
 * MSAL should be instantiated outside the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
export const msalInstance = new PublicClientApplication(msalConfig);

// Optional - This will update account state if a user signs in from another tab or window
if (msalConfig.cache?.cacheLocation === 'localStorage') {
  msalInstance.enableAccountStorageEvents();
}

export async function getTokenSilent(msalInstance, scopes) {
  let activeAccount = msalInstance.getActiveAccount();
  let accounts = msalInstance.getAllAccounts();

  if (!activeAccount) {
    // console.log('No active account');

    if (accounts.length > 0) {
      console.assert(accounts.length === 1, 'Multiple accounts found. Using first account.');
      activeAccount = accounts[0]; // or logic to select an account if multiple are found
      msalInstance.setActiveAccount(activeAccount);
    } else {
      // The following code will attempt to silently login, and then login interactively if that fails.
      console.warn('No active account or cached accounts found. Attempting silent login.');

      try {
        await msalInstance.initialize(); // NOTE: Logs indicated this is required for ssoSilent to work.
        const loginResponse = await msalInstance.ssoSilent(loginRequest);
        console.debug('Silent login response', loginResponse);
      } catch (ssoSilentError) {
        console.warn('Silent login failed', ssoSilentError);

        if (ssoSilentError instanceof InteractionRequiredAuthError) {
          // If silent requests fail, you'd typically fall back to an interactive method.
          // Here's using acquireTokenPopup as an example.
          // console.log('Interaction login required. Using popup...');
          try {
            /*const authResult =*/ await msalInstance.loginPopup(loginRequest);
            // console.debug('Popup login response', authResult);
          } catch (popupError) {
            // console.error('Interactive login failed.', popupError);

            // NOTE: The following is intended to clear the session from the browser cache.
            // This would cause the user to be prompted to sign in again.
            await removeSessionAndReload();
            throw popupError; // NOTE: Page reloaded so this is redundant, but left here for clarity.
          }
        } else {
          console.debug('ssoSilentError', ssoSilentError);
          throw new Error('No active account and SSO silent login failed.');
        }
      }
    }
  }

  activeAccount = msalInstance.getActiveAccount();
  console.assert(activeAccount != null, 'No active account');

  accounts = msalInstance.getAllAccounts();
  console.assert(accounts.length !== 0, 'Zero accounts available');
  console.assert(accounts.length <= 1, 'Multiple accounts found', accounts);

  const request = {
    account: accounts[0],
    scopes,
  };

  let accessToken;

  try {
    await msalInstance.initialize();
    const silentResponse = await msalInstance.acquireTokenSilent(request);
    accessToken = silentResponse.accessToken;
  } catch (tokenSilentError) {
    console.warn('Silent token acquisition failed', tokenSilentError);

    // If silent requests fail, you'd typically fall back to an interactive method.
    // console.log('Interaction login required. Using popup...');
    try {
      const popupResponse = await msalInstance.acquireTokenPopup(request);
      accessToken = popupResponse.accessToken;
    } catch (popupError) {
      console.error('Interactive token acquisition failed.', popupError);

      // NOTE: The following is intended to clear the session from the browser cache.
      // This would cause the user to be prompted to sign in again.
      await removeSessionAndReload();
      throw popupError; // NOTE: Page reloaded so this is redundant, but left here for clarity.
    }
  }

  return accessToken;
}

/**
 * This function skips server sign-out, and simply clears local cache of user tokens.
 * Page is reloaded to ensure all state is cleared.
 */
export async function removeSessionAndReload() {
  await msalInstance.logoutRedirect({
    onRedirectNavigate: (/*url*/) => {
      window.location.reload(); // Reloading page
      return false; // This stops navigation after local logout
    },
  });
}
